import React, { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Mail, Phone, MessageCircle, Clock, MapPin, Flag, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/card";
import ContactInfoModal from "../../components/ui/ContactInfoModal";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { 
    fetchContactInfos, 
    deleteContactInfo, 
    clearContactInfoError,
    clearCurrentContactInfo 
} from "../../redux/Slices/contactInfoSlice";
import { ContactInfoBackendResponse } from "../../modules/shared/model/contact-info.interfaces";

const ContactInfoManagement: React.FC = () => {
    const dispatch = useAppDispatch();
    const { 
        items: contactInfos, 
        loading, 
        error, 
        totalCount,
        hasNextPage,
        hasPreviousPage,
        page,
        limit 
    } = useAppSelector((state: any) => state.contactInfos);

    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedContactInfo, setSelectedContactInfo] = useState<ContactInfoBackendResponse | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");

    // Load contact infos on component mount
    useEffect(() => {
        dispatch(fetchContactInfos({
            page: 1,
            limit: 10,
        }));
    }, [dispatch]);

    // Handle search
    const handleSearch = (query: string) => {
        setSearchQuery(query);
        setCurrentPage(1);
        dispatch(fetchContactInfos({
            page: 1,
            limit: 10,
            search: query,
        }));
    };

    // Handle pagination
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        dispatch(fetchContactInfos({
            page: newPage,
            limit: 10,
            search: searchQuery,
        }));
    };

    // Handle load more
    const handleLoadMore = () => {
        if (hasNextPage) {
            dispatch(fetchContactInfos({
                page: currentPage + 1,
                limit: 10,
                search: searchQuery,
            }));
        }
    };

    // Handle edit contact info
    const handleEditContactInfo = (contactInfo: ContactInfoBackendResponse) => {
        setSelectedContactInfo(contactInfo);
    };

    // Handle delete contact info
    const handleDeleteContactInfo = async (id: string) => {
        if (window.confirm("Are you sure you want to delete this contact information?")) {
            try {
                await dispatch(deleteContactInfo(id)).unwrap();
                // Refresh the list
                dispatch(fetchContactInfos({
                    page: currentPage,
                    limit: 10,
                    search: searchQuery,
                }));
            } catch (error) {
                console.error("Failed to delete contact info:", error);
            }
        }
    };

    // Handle modal close
    const handleModalClose = () => {
        setShowAddModal(false);
        setSelectedContactInfo(null);
        dispatch(clearCurrentContactInfo());
    };

    // Handle success after create/update
    const handleSuccess = () => {
        // Refresh the list
        dispatch(fetchContactInfos({
            page: currentPage,
            limit: 10,
            search: searchQuery,
        }));
    };

    // Clear error on component unmount
    useEffect(() => {
        return () => {
            dispatch(clearContactInfoError());
        };
    }, [dispatch]);

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <div>
                    <h2 className="text-lg sm:text-xl font-semibold">Contact Information Management</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Manage contact information for different countries
                    </p>
                </div>
                <Button onClick={() => setShowAddModal(true)} className="w-full sm:w-auto text-sm">
                    <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />Add Contact Info
                </Button>
            </div>

            {/* Search */}
            <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                    <input
                        type="text"
                        placeholder="Search contact information..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
                <div className="text-sm text-gray-600 flex items-center">
                    {totalCount} total contact{totalCount !== 1 ? "s" : ""}
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            {/* Loading State */}
            {loading && contactInfos.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                    <span className="ml-3 text-gray-500">Loading contact information...</span>
                </div>
            ) : contactInfos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <MapPin className="h-12 w-12 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Contact Information Found</h3>
                    <p className="text-sm text-gray-400 mb-4">
                        {searchQuery ? "Try adjusting your search criteria" : "Get started by adding contact information"}
                    </p>
                    {!searchQuery && (
                        <Button onClick={() => setShowAddModal(true)}>
                            <Plus className="h-4 w-4 mr-2" />Add First Contact Info
                        </Button>
                    )}
                </div>
            ) : (
                <>
                    {/* Contact Info List */}
                    <div className="grid grid-cols-1 gap-4 sm:gap-6">
                        {contactInfos.map((contactInfo: ContactInfoBackendResponse) => (
                            <Card key={contactInfo.id} className="p-4 sm:p-6">
                                <div className="flex flex-col sm:flex-row justify-between items-start">
                                    <div className="mb-4 sm:mb-0">
                                        <div className="flex items-center gap-2">
                                            <Flag className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                                            <h3 className="text-base sm:text-lg font-semibold">
                                                {contactInfo.name || "Unnamed Contact"}
                                            </h3>
                                        </div>
                                        <div className="mt-2 text-xs sm:text-sm text-gray-600">
                                            {contactInfo.country?.name} - {contactInfo.country?.code}
                                            {contactInfo.vatNumber && ` - VAT: ${contactInfo.vatNumber}`}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            onClick={() => handleEditContactInfo(contactInfo)}
                                        >
                                            <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                                        </Button>
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="text-red-600 hover:text-red-700"
                                            onClick={() => handleDeleteContactInfo(contactInfo.id)}
                                        >
                                            <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Contact Details */}
                                    <div>
                                        <h4 className="font-medium mb-2 text-sm sm:text-base">Contact Details</h4>
                                        <div className="space-y-2 text-xs sm:text-sm">
                                            {contactInfo.email && (
                                                <div className="flex items-center gap-2">
                                                    <Mail className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                                                    <span>{contactInfo.email}</span>
                                                </div>
                                            )}
                                            {contactInfo.phone && (
                                                <div className="flex items-center gap-2">
                                                    <Phone className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                                                    <span>{contactInfo.phone}</span>
                                                </div>
                                            )}
                                            {contactInfo.whatsapp && (
                                                <div className="flex items-center gap-2">
                                                    <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                                                    <span>{contactInfo.whatsapp}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Business Hours */}
                                    <div>
                                        <h4 className="font-medium mb-2 text-sm sm:text-base">Business Hours</h4>
                                        <div className="space-y-2 text-xs sm:text-sm">
                                            {contactInfo.weekdayHours && (
                                                <div className="flex items-center gap-2">
                                                    <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                                                    <span>Weekday: {contactInfo.weekdayHours}</span>
                                                </div>
                                            )}
                                            {contactInfo.weekendHours && (
                                                <div className="flex items-center gap-2">
                                                    <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                                                    <span>Weekend: {contactInfo.weekendHours}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Address */}
                                {(contactInfo.address || contactInfo.city || contactInfo.state) && (
                                    <div className="mt-4">
                                        <h4 className="font-medium mb-2 text-sm sm:text-base">Address</h4>
                                        <div className="flex items-start gap-2 text-xs sm:text-sm">
                                            <MapPin className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400 mt-1" />
                                            <div>
                                                {contactInfo.address && <div>{contactInfo.address}</div>}
                                                {(contactInfo.city || contactInfo.state) && (
                                                    <div>
                                                        {contactInfo.city && contactInfo.state 
                                                            ? `${contactInfo.city}, ${contactInfo.state}` 
                                                            : contactInfo.city || contactInfo.state
                                                        }
                                                        {contactInfo.postalCode && ` ${contactInfo.postalCode}`}
                                                    </div>
                                                )}
                                                {contactInfo.country && <div>{contactInfo.country.name}</div>}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Card>
                        ))}
                    </div>

                    {/* Load More Button */}
                    {hasNextPage && (
                        <div className="flex justify-center pt-4">
                            <Button
                                variant="outline"
                                onClick={handleLoadMore}
                                disabled={loading}
                                className="flex items-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Loading more...
                                    </>
                                ) : (
                                    <>
                                        Load More ({totalCount - contactInfos.length} remaining)
                                    </>
                                )}
                            </Button>
                        </div>
                    )}
                </>
            )}

            {/* Modals */}
            <ContactInfoModal
                isOpen={showAddModal}
                onClose={handleModalClose}
                onSuccess={handleSuccess}
            />

            <ContactInfoModal
                isOpen={!!selectedContactInfo}
                onClose={handleModalClose}
                contactInfo={selectedContactInfo}
                onSuccess={handleSuccess}
            />
        </div>
    );
};

export default ContactInfoManagement;
